import { useState } from "react";
import axios from "axios";
import { storage } from "../utils/storage";
import { useUserStore } from "../store/userStore";

const baseUrl = process.env.REACT_APP_API_CONFIG;

interface AuthResponse {
  data: {
    jwt: string;
    user: {
      id: number;
      username: string;
      email: string;
    };
  };
  code: number;
  message: string;
}

interface RegisterResponse {
  jwt: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
}

export default function useEmailAuth() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fetchProfile = useUserStore((state) => state.fetchProfile);

  /**
   * Register new user with email and password
   */
  const register = async (email: string, password: string) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post<RegisterResponse>(
        `${baseUrl}/auth/local/register`,
        {
          username: email, // Use email as username
          email,
          password,
        }
      );

      const { jwt, user } = response.data;
      const refreshToken = (response.data as any).refresh_token;

      // Save token and user email
      storage.setJwtToken(jwt);
      if (refreshToken) storage.setRefreshToken(refreshToken);
      storage.setUserEmail(user.email);

      // Fetch full profile
      await fetchProfile();

      return { success: true, user, error: null };
    } catch (err: any) {
      let apiError = "Registration failed. Please try again.";

      if (err?.response?.data?.error?.message) {
        apiError = err.response.data.error.message;
      } else if (err?.response?.data?.message) {
        // Handle different error formats
        const messages = err.response.data.message;
        if (Array.isArray(messages)) {
          apiError = messages.map((m: any) => m.messages?.[0]?.message || m).join(", ");
        } else {
          apiError = messages;
        }
      }

      // Translate common errors
      if (apiError.includes("Email or Username are already taken")) {
        apiError = "This email is already registered. Please login instead.";
      }

      setError(apiError);
      return { success: false, user: null, error: apiError };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login with email and password
   */
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post<AuthResponse>(
        `${baseUrl}/auth/local`,
        {
          identifier: email, // Strapi uses 'identifier' for login
          password,
        }
      );

      const { jwt, user } = response.data.data;
      const refreshToken = (response.data.data as any).refresh_token;

      // Save token and user email
      storage.setJwtToken(jwt);
      if (refreshToken) storage.setRefreshToken(refreshToken);
      storage.setUserEmail(user.email);

      // Fetch full profile
      await fetchProfile();

      return { success: true, user, error: null };
    } catch (err: any) {
      let apiError = "Login failed. Please check your credentials.";

      if (err?.response?.data?.error?.message) {
        apiError = err.response.data.error.message;
      }

      // Translate common errors
      if (apiError.includes("Invalid identifier or password")) {
        apiError = "Invalid email or password.";
      } else if (apiError.includes("Your account email is not confirmed")) {
        apiError = "Please verify your email before logging in.";
      }

      setError(apiError);
      return { success: false, user: null, error: apiError };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError("");

  return { register, login, loading, error, clearError };
}
